import React from "react";
import {
  Download,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
} from "lucide-react";

const DietPlans = () => {
  const meals = [
    {
      tag: "Breakfast",
      title: "Berry Oatmeal Bowl",
      desc: "Rolled oats cooked with almond milk, topped with fresh mixed berries.",
      img: "https://images.unsplash.com/photo-1517673400267-0251440c45dc?w=600&q=80",
      cal: 320,
      p: 12,
      c: 45,
      f: 8,
      done: true,
    },
    {
      tag: "Lunch",
      title: "Grilled Chicken Salad",
      desc: "Lean grilled chicken breast on a bed of mixed greens, cherry tomatoes.",
      img: "https://images.unsplash.com/photo-1546793665-c74683f339c1?w=600&q=80",
      cal: 450,
      p: 40,
      c: 35,
      f: 15,
      done: true,
    },
    {
      tag: "Dinner",
      title: "Baked Salmon & Asparagus",
      desc: "Wild-caught salmon baked with lemon herbs",
      img: "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=600&q=80",
      cal: 520,
      p: 38,
      c: 42,
      f: 22,
      done: false,
    },
    {
      tag: "Snack",
      title: "Greek Yogurt & Almonds",
      desc: "Plain non-fat Greek yogurt drizzled with raw honey ",
      img: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=600&q=80",
      cal: 210,
      p: 18,
      c: 15,
      f: 10,
      done: false,
    },
  ];

  const truncateTitle = (title, maxLength = 14) => {
    if (title.length <= maxLength) return title;
    return title.slice(0, maxLength) + "...";
  };

  return (
    <div className="w-full font-lato">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight">
            Diet Plans
          </h2>
          <p className="text-[#98a19b] text-sm mt-2">
            View your active nutrition plan, log your meals, and track your
            adherence.
          </p>
        </div>

        <button className="h-[42px] px-5 rounded-md border border-[#dfe5df] text-[13px] text-[#4f5b56] hover:bg-gray-50 flex items-center gap-2 w-fit">
          <Download size={14} />
          Download PDF
        </button>
      </div>
      <div className="bg-white border border-[#e8ece8] rounded-xl p-6 mb-8">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-[#58b96b] text-white text-[11px] font-medium">
                Active
              </span>
              <span className="text-[12px] text-[#98a19b]">
                Started: Sep 1, 2024
              </span>
            </div>

            <h3 className="text-xl sm:text-xl font-semibold text-[#24312d] leading-tight mb-3">
              Metabolic Reset & Fat Loss
            </h3>

            <p className="text-sm text-[#98a19b] leading-6 max-w-[560px] mb-6">
              A balanced, high-protein plan designed to optimize metabolism
              while maintaining lean muscle mass.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div>
                <p className="text-sm text-[#98a19b] mb-1">Daily Target</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  1,850 kcal
                </h4>
              </div>

              <div>
                <p className="text-sm text-[#98a19b] mb-1">Protein</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  140g
                </h4>
              </div>

              <div>
                <p className="text-sm text-[#98a19b] mb-1">Carbs</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  160g
                </h4>
              </div>

              <div>
                <p className="text-sm text-[#98a19b] mb-1">Fats</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  65g
                </h4>
              </div>
            </div>
          </div>

          <div className="xl:border-l xl:border-[#edf1ed] xl:pl-8">
            <div className="mb-7">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[#24312d]">
                  Plan Progress (Week 4 of 12)
                </span>
                <span className="text-[13px] font-semibold text-[#24312d]">
                  33%
                </span>
              </div>

              <div className="h-[6px] bg-[#edf1ed] rounded-full overflow-hidden">
                <div className="w-[33%] h-full bg-[#58b96b] rounded-full"></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[#24312d]">
                  Weekly Adherence
                </span>
                <span className="text-[13px] font-semibold text-[#58b96b]">
                  85% (Excellent)
                </span>
              </div>

              <div className="h-[6px] bg-[#edf1ed] rounded-full overflow-hidden">
                <div className="w-[85%] h-full bg-[#58b96b] rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <h3 className="text-[22px] font-semibold text-[#24312d]">
          Today's Meals
        </h3>

        <div className="flex items-center gap-2">
          <button className="w-9 h-9 border border-[#dfe5df] rounded-md flex items-center justify-center hover:bg-gray-50">
            <ChevronLeft size={15} />
          </button>

          <button className="h-9 px-4 border border-[#dfe5df] rounded-md text-[13px] text-[#4f5b56]">
            Today, Oct 22
          </button>

          <button className="w-9 h-9 border border-[#dfe5df] rounded-md flex items-center justify-center hover:bg-gray-50">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {meals.map((meal, index) => (
          <div
            key={index}
            className="bg-white border border-[#e8ece8] rounded-xl overflow-hidden"
          >
            <div className="relative h-[190px]">
              <img
                src={meal.img}
                alt={meal.title}
                className="w-full h-full object-cover"
              />

              <span className="absolute top-3 left-3 bg-white text-[#24312d] text-[11px] px-3 py-1 rounded-full font-medium">
                {meal.tag}
              </span>
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="text-[20px] font-semibold text-[#24312d] leading-snug">
                  {truncateTitle(meal.title)}
                </h4>

                {meal.done ? (
                  <CheckCircle2
                    size={18}
                    className="text-[#58b96b] mt-1 shrink-0"
                  />
                ) : (
                  <Circle
                    size={18}
                    className="text-[#98a19b] mt-1 shrink-0"
                  />
                )}
              </div>

              <p className="text-[13px] text-[#98a19b] leading-6 mb-4">
                {meal.desc}
              </p>

              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                  Cal: {meal.cal}
                </span>
                <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                  P: {meal.p}g
                </span>
                <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                  C: {meal.c}g
                </span>
                <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                  F: {meal.f}g
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button className="h-[38px] border border-[#dfe5df] rounded-md text-[13px] text-[#58b96b] hover:bg-[#f8fcf9]">
                  Swap
                </button>

                <button
                  className={`h-[38px] rounded-md text-[13px] ${
                    meal.done
                      ? "bg-[#f4faf5] text-[#57b26a]"
                      : "bg-[#57b26a] text-white"
                  }`}
                >
                  {meal.done ? "Logged" : "Log Meal"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DietPlans;